/**
 * RecapCardItem.f.jsx (SBR-3529 Phase 2-3 / Challenger 2 recap card component)
 * Renders a single related search term with a 2×2 grid of product thumbnail images.
 * Handles placeholder images for missing/failed slots and click-to-search navigation.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Image } from 'components/ui';
import { PLACEHOLDER_IMAGE_SRC } from 'components/RelatedSearches/constants';
import { space, colors, mediaQueries } from 'style/config';
import UI from 'utils/UI';
import localeUtils from 'utils/LanguageLocale';
import relatedSearchesEN from 'components/RelatedSearches/locales/relatedSearches_en_US';
import relatedSearchesFR from 'components/RelatedSearches/locales/relatedSearches_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(relatedSearchesEN, relatedSearchesFR);

/**
 * RecapCardItem: 2×2 grid card for recap variant.
 * Phase 2: Grid layout with placeholder handling
 * Phase 3: Term label + click handler with debounce (inherited from parent)
 * Phase 6: Accessibility (aria-label, keyboard focusable)
 * isLoading: term/label render immediately (known up front); only the 4 image slots
 * shimmer individually until the multiTermSearch fetch resolves with real thumbnails.
 */
function RecapCardItem({
    term, images, onClick, _isActive, isLoading = false
}) {
    // Phase 2 (P2-001 through P2-004): Cap to 4 images, fill remaining slots with placeholder
    // (BR-014: never hide the card for missing images — missing slots show the Sephora logo
    // placeholder with white background).
    const gridImages = images.slice(0, 4); // P2-004: cap to first 4
    const slots = Array(4)
        .fill(null)
        .map((_, i) => gridImages[i] || null);

    // Phase 3 (P3-002): Click handler navigates via parent's debounced handleTermClick
    const handleClick = () => {
        onClick(term);
    };

    // Phase 6 (P6-002) + Phase 7 (P7-003): Localized aria-label
    const ariaLabel = getText('searchForTerm', [term]);

    return (
        <Box
            data-testid='recap-card-item'
            data-cnstrc-related-search-term={term}
            css={styles.card}
        >
            <button
                type='button'
                css={styles.button}
                onClick={handleClick}
                aria-label={ariaLabel} // P6-002: aria-label for accessibility
            >
                {/* Phase 3 (P3-001): Term label above grid — known up front, never blocked by the image fetch */}
                <Text
                    data-testid='recap-card-term'
                    fontSize='base'
                    fontWeight='normal'
                    lineHeight='tight'
                    css={styles.term}
                    children={term}
                />

                {/* Phase 2 (P2-001): 2×2 grid layout */}
                <Box
                    data-testid='recap-card-grid'
                    role={isLoading ? 'status' : undefined}
                    aria-busy={isLoading}
                    aria-label={isLoading ? getText('loadingRelatedSearches') : undefined}
                    css={styles.grid}
                >
                    {slots.map((img, i) => (
                        <Box
                            key={i}
                            data-testid={`image-slot-${i}`}
                            css={[styles.imageSlot, !img && styles.imageSlotPlaceholder, isLoading && UI.SKELETON_ANIMATION]}
                        >
                            {!isLoading && (
                                <Image
                                    src={img?.url || PLACEHOLDER_IMAGE_SRC}
                                    alt={img?.altText || ''}
                                    css={styles.image}
                                />
                            )}
                        </Box>
                    ))}
                </Box>
            </button>
        </Box>
    );
}

// Co-located styles (UFE convention)
const styles = {
    card: {
        listStyle: 'none',
        backgroundColor: colors.white,
        boxShadow: '0px 0px 6px 0px #00000033',
        borderRadius: space[1], // 4px
        maxWidth: '146px', // SMUI card size per Figma spec
        '&:hover': {
            boxShadow: '0px 0px 8px 0px #00000040'
        },
        [mediaQueries.sm]: {
            maxWidth: '198px' // LGUI card size per Figma spec
        }
    },
    button: {
        paddingTop: space[4],
        paddingBottom: space[4],
        paddingLeft: space[3], // SMUI: 12px left/right per Figma spec
        paddingRight: space[3],
        border: 'none',
        backgroundColor: 'transparent',
        borderRadius: 0,
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: space[4],
        width: '100%',
        boxSizing: 'border-box',
        '&:focus': {
            outline: `2px solid ${colors.black}`,
            outlineOffset: space[1]
        },
        [mediaQueries.sm]: {
            paddingLeft: space[4], // LGUI: 16px left/right per Figma spec
            paddingRight: space[4]
        }
    },
    term: {
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: 2, // max 2 lines
        WebkitBoxOrient: 'vertical',
        textOverflow: 'ellipsis',
        lineHeight: '16px', // 16px line-height per Figma spec
        // Reserve space for 2 lines always, so the image grid starts at the same offset
        // on every card regardless of whether this term wraps to 1 or 2 lines.
        height: space[6],
        flexShrink: 0,
        fontWeight: 400 // Regular weight, not bold
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '59px 59px', // SMUI: 59px per image per Figma spec
        gap: space[1], // SMUI: 4px spacing between grid items
        [mediaQueries.sm]: {
            gridTemplateColumns: '78px 78px', // LGUI: 78px per image per Figma spec
            gap: 10 // LGUI: 10px spacing between grid items
        }
    },
    imageSlot: {
        position: 'relative',
        width: '59px',
        height: '59px',
        overflow: 'hidden',
        backgroundColor: colors.gray[100], // Light gray background behind real thumbnails
        borderRadius: 0,
        [mediaQueries.sm]: {
            width: '78px',
            height: '78px'
        }
    },
    // Placeholder slots show Sephora logo on white background
    imageSlotPlaceholder: {
        backgroundColor: colors.white
    },
    image: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover' // P2-005: maintain aspect ratio, cover slot
    }
};

RecapCardItem.propTypes = {
    term: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string.isRequired,
            altText: PropTypes.string
        })
    ).isRequired,
    onClick: PropTypes.func.isRequired,
    isActive: PropTypes.bool,
    isLoading: PropTypes.bool
};

export default wrapFunctionalComponent(RecapCardItem, 'RecapCardItem');
